import 'katex/dist/katex.min.css';

import React, { ErrorInfo, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { CustomBlockquote } from './custom-components/CustomBlockquote';
import { CustomCode, INLINE_CODE } from './custom-components/CustomCode';
import {
  CustomLink,
  CustomTable,
  CustomTableCell,
  CustomTableHeader,
} from './custom-components/CustomComponents';
import styles from './Markdown.module.scss';

export interface MarkdownProps {
  message: string;
  onLoad?: (element: HTMLDivElement) => void;
  components?: Partial<Components>;
  onError?: (error: unknown, info: ErrorInfo) => void;
  isDark?: boolean;
}

export const Markdown = ({
  message,
  onLoad,
  components,
  onError,
  isDark = true,
}: MarkdownProps) => {
  // This is fix for table markdown. Need to check if BE/DS can fix the GPT response to not include underscore with tables
  // Discussion: https://toggleglobal.slack.com/archives/C053NT28Q8M/p1686422095030189
  const formattedMarkdown = message.replace(/\|_/g, '| ');

  const markdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onLoad && markdownRef.current) {
      onLoad(markdownRef.current);
    }
  }, [message]);

  return (
    <ErrorBoundary fallback={null} onError={onError}>
      <div
        ref={markdownRef}
        className={styles.markdownStyles}
        data-markdown
        data-testid="markdown"
      >
        <ReactMarkdown
          remarkPlugins={[
            [remarkGfm, { singleTilde: false }],
            [remarkMath, { singleDollarTextMath: false }],
          ]}
          rehypePlugins={[rehypeKatex]}
          components={{
            a: CustomLink,
            td: CustomTableCell,
            th: CustomTableHeader,
            table: CustomTable,
            code: ({ children, className = INLINE_CODE }) => {
              return (
                <CustomCode isDark={isDark} language={className}>
                  {children}
                </CustomCode>
              );
            },
            blockquote: CustomBlockquote,
            p: ({ children, node }) => {
              const hasBlock =
                node != null &&
                node.children.some(
                  c =>
                    c.type === 'element' &&
                    ['table', 'div', 'ul', 'ol', 'pre', 'blockquote'].includes(
                      (c as { tagName: string }).tagName
                    )
                );
              return hasBlock ? <div>{children}</div> : <p>{children}</p>;
            },
            ...components,
          }}
        >
          {formattedMarkdown}
        </ReactMarkdown>
      </div>
    </ErrorBoundary>
  );
};
